import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import type { WheelSegment } from './types';
import { resolveBrandFromSurveyObject, type ScratchCardBrand } from './ScratchCardCanvas';

/**
 * Xinkit — Xotski drop-and-bounce peg game (Gate 4).
 *
 * "Plinko" is a TV game-show trademark and is retired from the product entirely.
 * This game ships consumer-facing as Xinkit; the internal gameType is `xinkit`.
 *
 * Design law (game-engine.md §2): the brand is structural, not decorative.
 * The brand rides the BALL the guest drops and the PEGS it strikes; it owns the
 * BACK WALL (etched wordmark) and the FRAME marquee. The BUCKETS are the prize
 * surface (prize-tier color). See docs/prds/apps/xotski/_reference/PRD_Xotski_Xinkit_Game.md.
 *
 * Which prize is NOT decided here. The wrapper picks the winner with the shared
 * prize engine (calculateWinningSegment, (6-rating)²) and passes it as `prize`.
 * The prize maps to a target bucket; the drop is an AUTHORED deflection path
 * that lands believably in that bucket. The physics constants govern only the
 * LOOK of each bounce, never the direction at a decision point — there is no
 * per-frame fight between physics and a bias (the honest-scatter model).
 *
 * The guest ALWAYS wins something. Corners are the lowest tier, never a loss.
 *
 * Ported from the working prototype: _reference/Xinkit -standalone-.html.
 */

export type XinkitPhase = 'idle' | 'dropping' | 'inFlight' | 'landed' | 'winReveal';
/** The brand vehicle: logo (brand mark on the face) · cap (bottle-cap, beverage) · solid (brand fill). */
export type BallVariant = 'logo' | 'cap' | 'solid';

export interface XinkitProps {
  /** The prize the engine already chose. The guest always wins something. */
  prize: WheelSegment;
  /** Resolved 5-color brand. Use resolveBrandFromSurveyObject() for the 3-color shape. */
  brand: ScratchCardBrand;
  language?: 'en' | 'es';
  reducedMotion?: boolean;
  /** Force a visual phase for Storybook state stories (skips interaction). */
  demoPhase?: XinkitPhase;
  /** Ball look — the primary brand vehicle (default 'logo'). */
  ballVariant?: BallVariant;
  /** Back-wall etched wordmark (the activation press-wall surface). */
  wordmark?: string;
  /** Optional tagline etched under the wordmark on the back wall. */
  tagline?: string;
  /** Optional manager override of the center-out tier band (length 9). */
  bucketTiers?: number[];
  /** Fires once when the bucket resolves, with the won prize and its bucket index. */
  onRevealed?: (prize: WheelSegment, bucketIndex: number) => void;
}

// Re-export so the registry can import the resolver from one place.
export { resolveBrandFromSurveyObject };
export type { ScratchCardBrand };

// ── Board geometry (PRD §2 / prototype §7.3) ──
const BW = 358, BH = 500;
const ROWS = 8, BUCKETS = 9;
const HEAD_H = 42;                 // brand header + launcher strip
const PEG_R = 5.4, BALL_R = 9.2;
const ROW_TOP = 92, ROW_GAP = 40;  // peg rows at 92,132,…,372
const BUCKET_H = 70, BUCKET_Y = BH - BUCKET_H;
const DROP_Y = HEAD_H + 18;        // ball entry y
const BKT_W = BW / BUCKETS;
const laneX = (l: number) => (l + 0.5) * BKT_W;  // lane 0..8 center → x
const pegRowY = (r: number) => ROW_TOP + r * ROW_GAP;

// ── Prize-tier colors (the PRIZE surface — bucket fill / stars / burst). Fixed,
// semantic rarity palette, like the wheel's tiers; not brand-derived by design. ──
const TIER: Record<number, { base: string; hi: string }> = {
  5: { base: '#A8182B', hi: '#E23B4E' },
  4: { base: '#B5560E', hi: '#EA8A2A' },
  3: { base: '#9A7212', hi: '#E0A91E' },
  2: { base: '#10705A', hi: '#1FA37E' },
  1: { base: '#274C97', hi: '#3F6FD0' },
};
const XOTSKI_LIME = '#C8E530'; // canonical Xotski lime (watermark only)

// ── Color helpers (shared idiom with SoccerBall) ──
function hexRgb(hex: string): [number, number, number] {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
const clampByte = (c: number) => Math.max(0, Math.min(255, Math.round(c)));
function rgbHex(r: number, g: number, b: number) {
  return '#' + [r, g, b].map((c) => clampByte(c).toString(16).padStart(2, '0')).join('');
}
function darken(hex: string, factor = 0.55) {
  const [r, g, b] = hexRgb(hex);
  return rgbHex(r * factor, g * factor, b * factor);
}
function lighten(hex: string, amt = 0.35) {
  const [r, g, b] = hexRgb(hex);
  return rgbHex(r + (255 - r) * amt, g + (255 - g) * amt, b + (255 - b) * amt);
}
function toRgba(hex: string, a: number) {
  const [r, g, b] = hexRgb(hex);
  return `rgba(${r},${g},${b},${a})`;
}
/** Relative luminance 0..1 — used to keep structural brand colors legible on the dark board. */
function relLum(hex: string) {
  const [r, g, b] = hexRgb(hex);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}
/**
 * The structural brand color for pegs / ball / launcher / frame — must read on the
 * dark board. Picks the brighter of primary/secondary, and lightens it if even that
 * is too dark (e.g. Modelo navy, Clean near-black), so the brand never collapses
 * into the background. Keeps the brand hue.
 */
function structColor(primary: string, secondary: string) {
  const base = relLum(secondary) > relLum(primary) ? secondary : primary;
  return relLum(base) >= 0.22 ? base : lighten(base, 0.5);
}
function mix(a: string, b: string, t: number) {
  const [r1, g1, b1] = hexRgb(a), [r2, g2, b2] = hexRgb(b);
  return rgbHex(r1 + (r2 - r1) * t, g1 + (g2 - g1) * t, b1 + (b2 - b1) * t);
}
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

function rr(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// ── Sound (Web Audio, lazily started on the Drop gesture) + haptics ──
let AC: AudioContext | null = null;
let MASTER: GainNode | null = null;
let TENSION: { o: OscillatorNode; g: GainNode; lfo: OscillatorNode } | null = null;
function audioCtx(): AudioContext | null {
  if (!AC) {
    try {
      const C = window.AudioContext || (window as any).webkitAudioContext;
      AC = new C(); MASTER = AC.createGain(); MASTER.gain.value = 0.42; MASTER.connect(AC.destination);
    } catch { AC = null; }
  }
  if (AC && AC.state === 'suspended') AC.resume();
  return AC;
}
function blip(freq: number, dur: number, type: OscillatorType = 'triangle', vol = 0.18, glideTo?: number) {
  if (!AC || !MASTER) return;
  const t = AC.currentTime;
  const o = AC.createOscillator(), g = AC.createGain();
  o.type = type; o.frequency.setValueAtTime(freq, t);
  if (glideTo) o.frequency.exponentialRampToValueAtTime(Math.max(40, glideTo), t + dur);
  g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(vol, t + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g); g.connect(MASTER); o.start(t); o.stop(t + dur + 0.02);
}
const sndPeg = (speed: number) => { if (audioCtx()) blip(480 + Math.min(1, speed / 4.2) * 620, 0.06, 'triangle', 0.11); };
const sndThud = () => { if (audioCtx()) blip(165, 0.20, 'sine', 0.32, 80); };
const sndWin = () => { if (audioCtx()) [0, 4, 7, 12].forEach((s, i) => setTimeout(() => blip(523.25 * Math.pow(2, s / 12), 0.26, 'triangle', 0.16), i * 88)); };
function sndTensionStart() {
  if (!audioCtx() || TENSION || !AC || !MASTER) return;
  const t = AC.currentTime;
  const o = AC.createOscillator(), g = AC.createGain(), lfo = AC.createOscillator(), lg = AC.createGain();
  o.type = 'sine'; o.frequency.setValueAtTime(210, t); o.frequency.exponentialRampToValueAtTime(320, t + 0.9);
  lfo.type = 'sine'; lfo.frequency.value = 7; lg.gain.value = 4.5; lfo.connect(lg); lg.connect(o.frequency);
  g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.10, t + 0.10);
  o.connect(g); g.connect(MASTER); o.start(t); lfo.start(t); TENSION = { o, g, lfo };
}
function sndTensionStop() {
  if (!AC || !TENSION) return;
  const t = AC.currentTime; const { o, g, lfo } = TENSION;
  try { g.gain.exponentialRampToValueAtTime(0.0001, t + 0.12); o.stop(t + 0.15); lfo.stop(t + 0.15); } catch { /* noop */ }
  TENSION = null;
}
const buzz = (p: number | number[]) => { try { navigator.vibrate?.(p); } catch { /* noop */ } };

// ── Static peg field — even rows 9 pegs (lanes 0..8), odd rows 8 (lanes 0.5..7.5) ──
interface Peg { x: number; y: number; row: number; lane: number; }
function buildPegs(): Peg[] {
  const pegs: Peg[] = [];
  for (let r = 0; r < ROWS; r++) {
    const even = r % 2 === 0;
    const n = even ? 9 : 8;
    for (let c = 0; c < n; c++) {
      const lane = even ? c : c + 0.5;
      pegs.push({ x: laneX(lane), y: pegRowY(r), row: r, lane });
    }
  }
  return pegs;
}
const PEGS = buildPegs();
function nearestPeg(x: number, y: number): number {
  let bi = 0, bd = 1e9;
  PEGS.forEach((p, i) => { const d = (p.x - x) ** 2 + (p.y - y) ** 2; if (d < bd) { bd = d; bi = i; } });
  return bi;
}

// ── The honest-scatter model (PRD §3-4) ──
// center (4) = highest tier, fanning symmetrically out to corners = lowest tier.
const defaultTier = (i: number) => 5 - Math.abs(i - 4); // 1,2,3,4,5,4,3,2,1

function targetBucket(rating: number, aimLane: number, tierAt: (i: number) => number): number {
  const cands: number[] = [];
  for (let i = 0; i < BUCKETS; i++) if (tierAt(i) === rating) cands.push(i);
  if (!cands.length) return 4;
  cands.sort((a, b) => Math.abs(a - aimLane) - Math.abs(b - aimLane));
  return cands[0];
}

// Build the lane path S[0..8] (S[0]=4 center entry, S[8]=k). Each decision moves
// the lane ±0.5; rights = k. Drama-matched: high-tier center buckets swing toward
// an edge then correct late; low-tier buckets commit early.
function authorPath(k: number): number[] {
  const dramatic = defaultTier(k) >= 4; // center buckets 3,4,5
  let style: 'early' | 'late';
  if (dramatic) style = Math.random() < 0.5 ? 'early' : 'late';
  else if (k < 4) style = 'late';
  else style = 'early';
  const bits = style === 'early'
    ? Array.from({ length: ROWS }, (_, i) => (i < k ? 1 : 0))
    : Array.from({ length: ROWS }, (_, i) => (i >= ROWS - k ? 1 : 0));
  const S = [4]; let cum = 0;
  for (let m = 1; m <= ROWS; m++) { cum += bits[m - 1]; S.push(4 - 0.5 * m + cum); }
  return S; // length 9, S[8] === k
}

// Visible waypoints. Aim is feel-only: the first ~3 rows drift toward the aimed
// column, then the authored path takes over (PRD §3.4). Landing is never affected.
interface WP { x: number; y: number; }
function waypoints(S: number[], aimLane: number): WP[] {
  const aimW = [0.72, 0.42, 0.16]; // decays to 0 by row 3
  const wp: WP[] = [{ x: laneX(aimLane), y: DROP_Y }];
  for (let r = 0; r < ROWS; r++) {
    let x = laneX(S[r + 1]);
    if (r < aimW.length) x = lerp(x, laneX(aimLane), aimW[r]);
    wp.push({ x, y: pegRowY(r) });
  }
  wp.push({ x: laneX(S[ROWS]), y: BUCKET_Y + BUCKET_H * 0.5 }); // settle into bucket k
  return wp;
}

const T = {
  aim: { en: 'drag to aim', es: 'arrastra para apuntar' },
  aimArrows: { en: '← drag to aim →', es: '← arrastra para apuntar →' },
  drop: { en: 'Drop', es: 'Soltar' },
  hint: { en: 'Drag to aim, then drop', es: 'Apunta y suelta la pelota' },
  prize: { en: 'Prize!', es: '¡Premio!' },
} as const;

type Mode = 'idle' | 'dropping' | 'hang' | 'teeter' | 'settle' | 'landed' | 'reveal' | 'done' | 'demo';

interface Spark { x: number; y: number; vx: number; vy: number; life: number; col: string; sz: number; }

export const Xinkit: React.FC<XinkitProps> = ({
  prize,
  brand,
  language = 'en',
  reducedMotion = false,
  demoPhase,
  ballVariant = 'logo',
  wordmark = 'BRAND',
  tagline = '',
  bucketTiers,
  onRevealed,
}) => {
  const cvRef = useRef<HTMLCanvasElement>(null);
  const ball = useRef({ x: laneX(4), y: DROP_Y, vy: 1.5, squash: 0, trail: [] as WP[], active: false, landed: false, bucket: -1 });
  const aim = useRef(4);                    // aimed lane 0..8
  const path = useRef<WP[] | null>(null);
  const seg = useRef(0);
  const pegHit = useRef<Record<number, number>>({});
  const sparks = useRef<Spark[]>([]);
  const burst = useRef<Spark[]>([]);
  const landFill = useRef(0);
  const reveal = useRef(0);
  const targetK = useRef(-1);
  const decoyD = useRef(-1);
  const teeter = useRef<{ phase?: string; t0: number; sx: number; sy: number } | null>(null);
  const hangRow = useRef(-1);
  const hung = useRef(false);
  const mode = useRef<Mode>(demoPhase ? 'demo' : 'idle');
  const idleT = useRef(0);
  const firedRef = useRef(false);
  const logoImg = useRef<HTMLImageElement | null>(null);
  const [, force] = useState(0);
  const [canDrop, setCanDrop] = useState(true);

  // Manager-overridable center-out band (Locked Decision 4).
  const tierAt = useCallback(
    (i: number) => (bucketTiers && bucketTiers.length === BUCKETS ? bucketTiers[i] : defaultTier(i)),
    [bucketTiers]
  );
  const bucketColor = useCallback((i: number) => TIER[tierAt(i)].hi, [tierAt]);

  // Brand mark for the ball face (logo variant fallback) — initials from wordmark.
  const mark = useMemo(() => {
    const words = wordmark.trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return wordmark.slice(0, 2).toUpperCase() || 'X';
  }, [wordmark]);

  // Brand-derived scene palette (zero hardcoded brand hex). The board is a dark,
  // brand-tinted cabinet so white pegs/markings/text read on light AND dark brands.
  const P = useMemo(() => {
    // `struct` is guaranteed legible on the dark board (Modelo/Clean fall back to a
    // brighter shade); `primary` stays for subtle tints where dark is fine.
    const struct = structColor(brand.primary, brand.secondary);
    return {
      boardTop: mix(darken(brand.primary, 0.32), '#0c0a12', 0.32),
      boardBot: mix(darken(brand.primary, 0.16), '#0c0a12', 0.5),
      primary: brand.primary,
      struct,
      structDk: darken(struct, 0.5),
      secondary: brand.secondary,
      accent: brand.accent,
      fg: '#F5F3FF',                 // text on the dark board (always light)
      fgMuted: 'rgba(245,243,255,0.6)',
      fgSubtle: 'rgba(245,243,255,0.36)',
    };
  }, [brand]);

  // Preload the brand logo for the ball (logo variant).
  useEffect(() => {
    if (!brand.logo) { logoImg.current = null; return; }
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => { logoImg.current = im; force((n) => n + 1); };
    im.src = brand.logo;
    return () => { im.onload = null; };
  }, [brand.logo]);

  const prizeNameLabel = language === 'es' ? (prize?.nameTranslate || prize?.name) : prize?.name;

  // ── Ball renderer (the brand vehicle) ──
  const drawBall = useCallback((ctx: CanvasRenderingContext2D, x: number, y: number, r: number, alpha: number) => {
    ctx.save(); ctx.globalAlpha = alpha;
    const body = ctx.createRadialGradient(x - r * 0.32, y - r * 0.34, 0, x, y, r);
    if (ballVariant === 'solid') {
      body.addColorStop(0, mix(P.struct, '#ffffff', 0.5)); body.addColorStop(0.5, P.struct); body.addColorStop(1, P.structDk);
    } else {
      body.addColorStop(0, 'rgba(255,255,255,.7)'); body.addColorStop(0.4, P.secondary); body.addColorStop(1, P.structDk);
    }
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fillStyle = body; ctx.fill();
    if (ballVariant === 'cap') {
      ctx.strokeStyle = toRgba(P.structDk, 0.9); ctx.lineWidth = 1.4;
      for (let i = 0; i < 14; i++) {
        const a = (i / 14) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(x + Math.cos(a) * r * 0.78, y + Math.sin(a) * r * 0.78);
        ctx.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r); ctx.stroke();
      }
      ctx.beginPath(); ctx.arc(x, y, r * 0.72, 0, Math.PI * 2); ctx.strokeStyle = 'rgba(255,255,255,.3)'; ctx.lineWidth = 1; ctx.stroke();
    }
    ctx.strokeStyle = 'rgba(255,255,255,.45)'; ctx.lineWidth = 1.2; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.stroke();
    // logo variant — draw the brand logo clipped into the ball; fall back to the mark.
    if (ballVariant === 'logo' && r > 6) {
      const im = logoImg.current;
      if (im && im.complete && im.naturalWidth) {
        ctx.save();
        ctx.beginPath(); ctx.arc(x, y, r * 0.82, 0, Math.PI * 2); ctx.clip();
        const ratio = Math.min((r * 1.5) / im.naturalWidth, (r * 1.5) / im.naturalHeight);
        const dw = im.naturalWidth * ratio, dh = im.naturalHeight * ratio;
        ctx.drawImage(im, x - dw / 2, y - dh / 2, dw, dh);
        ctx.restore();
      } else {
        ctx.font = `700 ${Math.max(6, r * 0.7)}px 'Space Mono', monospace`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillStyle = 'rgba(255,255,255,.95)'; ctx.fillText(mark, x, y + 0.5);
      }
    }
    // specular highlight
    const sp = ctx.createRadialGradient(x - r * 0.3, y - r * 0.34, 0, x - r * 0.3, y - r * 0.34, r * 0.7);
    sp.addColorStop(0, 'rgba(255,255,255,.6)'); sp.addColorStop(1, 'transparent');
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fillStyle = sp; ctx.fill();
    ctx.restore();
  }, [ballVariant, P, mark]);

  const seedSparks = useCallback((x: number, y: number): Spark[] =>
    Array.from({ length: 7 }, () => {
      const a = Math.random() * Math.PI * 2, sp = 0.8 + Math.random() * 2.2;
      return { x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 0.6, life: 1, col: P.primary, sz: 1.4 + Math.random() * 1.8 };
    }), [P.primary]);
  const seedBurst = useCallback((k: number): Spark[] => {
    const col = bucketColor(k);
    return Array.from({ length: 30 }, () => {
      const a = Math.random() * Math.PI * 2, sp = 1.4 + Math.random() * 3.6;
      return { x: laneX(k), y: BUCKET_Y + 6, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 1.6, life: 1, col, sz: 2 + Math.random() * 3 };
    });
  }, [bucketColor]);

  // ── Draw ──
  const draw = useCallback(() => {
    const cv = cvRef.current; if (!cv) return;
    const ctx = cv.getContext('2d'); if (!ctx) return;
    ctx.clearRect(0, 0, BW, BH);
    const dp = demoPhase, m = mode.current, b = ball.current;
    const idle = m === 'idle' || dp === 'idle';
    const revealing = m === 'reveal' || dp === 'winReveal';

    // ── Board background (brand-tinted dark field) ──
    const bg = ctx.createLinearGradient(0, 0, 0, BH);
    bg.addColorStop(0, P.boardTop); bg.addColorStop(1, P.boardBot);
    ctx.fillStyle = bg; ctx.fillRect(0, 0, BW, BH);
    ctx.strokeStyle = toRgba(P.primary, 0.05); ctx.lineWidth = 1;
    for (let x = 0; x < BW; x += 30) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, BH); ctx.stroke(); }
    for (let y = 0; y < BH; y += 30) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(BW, y); ctx.stroke(); }
    const glow = ctx.createLinearGradient(0, BUCKET_Y - 70, 0, BH);
    glow.addColorStop(0, 'transparent'); glow.addColorStop(1, toRgba(P.struct, 0.2));
    ctx.fillStyle = glow; ctx.fillRect(0, BUCKET_Y - 70, BW, BH - (BUCKET_Y - 70));

    // ── Back wall (the activation press wall) — the brand LOGO if we have one,
    //    else an etched wordmark. Either way it reads on every brand. ──
    ctx.save(); ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const wallY = BH * 0.42;
    const logo = logoImg.current;
    // Use the logo image only for dark brands (whose logos are light/colored and
    // read on the dark cabinet). Light brands have dark logos that would vanish,
    // so they fall back to the bright etched wordmark.
    const logoReadsOnBoard = relLum(brand.font) >= 0.5;
    if (logo && logo.complete && logo.naturalWidth && logoReadsOnBoard) {
      const maxW = BW * 0.6, maxH = BH * 0.22;
      const ratio = Math.min(maxW / logo.naturalWidth, maxH / logo.naturalHeight);
      const dw = logo.naturalWidth * ratio, dh = logo.naturalHeight * ratio;
      ctx.globalAlpha = 0.3; // faded into the wall, press-wall style
      ctx.drawImage(logo, BW / 2 - dw / 2, wallY - dh / 2, dw, dh);
      ctx.globalAlpha = 1;
    } else {
      const wm = wordmark.toLowerCase();
      (ctx as any).letterSpacing = '6px';
      let fs = 84; ctx.font = `700 ${fs}px 'Space Mono', monospace`;
      while (ctx.measureText(wm).width > BW * 0.82 && fs > 24) { fs -= 2; ctx.font = `700 ${fs}px 'Space Mono', monospace`; }
      ctx.lineJoin = 'round';
      ctx.fillStyle = toRgba(P.struct, 0.08);
      ctx.fillText(wm, BW / 2, wallY);
      ctx.lineWidth = Math.max(1.3, fs * 0.02);
      ctx.strokeStyle = toRgba(P.struct, 0.26);
      ctx.strokeText(wm, BW / 2, wallY);
      if (tagline) {
        (ctx as any).letterSpacing = '3px'; ctx.font = "600 10px 'DM Sans', sans-serif";
        ctx.fillStyle = toRgba(P.fg, 0.1);
        ctx.fillText(tagline.toUpperCase(), BW / 2, wallY + fs * 0.5);
      }
      (ctx as any).letterSpacing = '0px';
    }
    ctx.restore();

    // ── Launcher guide + ghost ball (idle) ──
    if (idle) {
      const lx = laneX(aim.current);
      ctx.save(); ctx.setLineDash([4, 6]); ctx.strokeStyle = toRgba(P.accent, 0.3); ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.moveTo(lx, HEAD_H); ctx.lineTo(lx, BUCKET_Y - 2); ctx.stroke(); ctx.restore();
      const pulse = 1 + 0.07 * Math.sin(idleT.current * 3.0);
      const gg = ctx.createRadialGradient(lx, DROP_Y, 0, lx, DROP_Y, BALL_R * 3.4);
      gg.addColorStop(0, toRgba(P.accent, 0.3)); gg.addColorStop(1, 'transparent');
      ctx.beginPath(); ctx.arc(lx, DROP_Y, BALL_R * 3.4, 0, Math.PI * 2); ctx.fillStyle = gg; ctx.fill();
      drawBall(ctx, lx, DROP_Y, BALL_R * pulse, 0.85);
    }

    // ── Pegs (brand primary + glint, flash to accent on hit) ──
    PEGS.forEach((p, i) => {
      const hit = pegHit.current[i] || 0, hs = 1 + (hit / 8) * 0.3;
      if (hit > 0) {
        const fr = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, PEG_R * 4.4);
        fr.addColorStop(0, toRgba(P.accent, (hit / 8) * 0.55)); fr.addColorStop(1, 'transparent');
        ctx.beginPath(); ctx.arc(p.x, p.y, PEG_R * 4.4, 0, Math.PI * 2); ctx.fillStyle = fr; ctx.fill();
      }
      const base = hit > 0 ? mix(P.struct, P.accent, Math.min(1, hit / 8)) : P.struct;
      const pg = ctx.createRadialGradient(p.x - PEG_R * 0.35, p.y - PEG_R * 0.35, 0, p.x, p.y, PEG_R * hs);
      pg.addColorStop(0, 'rgba(255,255,255,.65)'); pg.addColorStop(0.4, base); pg.addColorStop(1, P.structDk);
      ctx.globalAlpha = idle ? 0.7 : 1;
      ctx.beginPath(); ctx.arc(p.x, p.y, PEG_R * hs, 0, Math.PI * 2); ctx.fillStyle = pg; ctx.fill();
      ctx.globalAlpha = 1;
    });

    // ── Buckets (prize-tier color) ──
    for (let i = 0; i < BUCKETS; i++) {
      const x = i * BKT_W, y = BUCKET_Y, w = BKT_W - 2, h = BUCKET_H - 2;
      const col = bucketColor(i), isLanded = b.landed && b.bucket === i;
      const fa = isLanded ? 0.16 + landFill.current * 0.4 : 0.12;
      ctx.fillStyle = toRgba(col, fa); rr(ctx, x + 1, y + 1, w, h, 5); ctx.fill();
      ctx.strokeStyle = isLanded ? col : toRgba(col, 0.3); ctx.lineWidth = isLanded ? 2 : 0.8; ctx.stroke();
      const t = tierAt(i);
      ctx.font = "700 9px 'DM Sans', sans-serif"; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = isLanded ? col : toRgba(col, 0.6);
      ctx.fillText('★'.repeat(t), x + BKT_W / 2, y + h * 0.66);
    }

    // ── Trail ──
    b.trail.forEach((pt, i) => {
      const a = (i / b.trail.length) * 0.2;
      ctx.beginPath(); ctx.arc(pt.x, pt.y, BALL_R * (i / b.trail.length) * 0.9, 0, Math.PI * 2);
      ctx.fillStyle = toRgba(P.secondary, a); ctx.fill();
    });

    // ── Sparks ──
    if (sparks.current.length) {
      sparks.current.forEach((s) => { ctx.globalAlpha = Math.max(0, s.life); ctx.fillStyle = s.col; ctx.beginPath(); ctx.arc(s.x, s.y, s.sz, 0, Math.PI * 2); ctx.fill(); });
      ctx.globalAlpha = 1;
    }

    // ── Ball (with squash & stretch) ──
    if (b.active) {
      const gg = ctx.createRadialGradient(b.x, b.y, BALL_R * 0.6, b.x, b.y, BALL_R + 12);
      gg.addColorStop(0, toRgba(P.secondary, 0.45)); gg.addColorStop(1, 'transparent');
      ctx.beginPath(); ctx.arc(b.x, b.y, BALL_R + 12, 0, Math.PI * 2); ctx.fillStyle = gg; ctx.fill();
      const st = Math.min(0.28, (b.vy || 0) * 0.05), sq = b.squash || 0;
      const sx = (1 - st * 0.7) * (1 + 0.34 * sq), sy = (1 + st) * (1 - 0.32 * sq);
      ctx.save(); ctx.translate(b.x, b.y); ctx.scale(sx, sy); ctx.translate(-b.x, -b.y);
      drawBall(ctx, b.x, b.y, BALL_R, 1);
      ctx.restore();
    }

    // ── Particle burst (landed/reveal) ──
    if (burst.current.length) {
      burst.current.forEach((p) => { ctx.globalAlpha = Math.max(0, p.life); ctx.fillStyle = p.col; ctx.beginPath(); ctx.arc(p.x, p.y, p.sz, 0, Math.PI * 2); ctx.fill(); });
      ctx.globalAlpha = 1;
    }

    // ── Win-reveal prize label rising from the bucket ──
    if (revealing && b.bucket >= 0) {
      const col = bucketColor(b.bucket), rise = Math.min(1, reveal.current);
      const cx = laneX(b.bucket), cy = BUCKET_Y - 6 - rise * 30, a = rise;
      const label = prizeNameLabel || T.prize[language];
      ctx.save(); ctx.globalAlpha = a; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.font = "800 13px 'DM Sans', sans-serif"; ctx.shadowColor = 'rgba(0,0,0,.5)'; ctx.shadowBlur = 4;
      ctx.fillStyle = '#fff'; ctx.fillText(label, cx, cy);
      ctx.shadowBlur = 0; ctx.font = "700 11px 'DM Sans', sans-serif"; ctx.fillStyle = col;
      ctx.fillText('★'.repeat(tierAt(b.bucket)), cx, cy + 15); ctx.restore();
    }

    // ── Header strip (brand) + launcher track ──
    ctx.fillStyle = toRgba(P.primary, 0.12); ctx.fillRect(0, 0, BW, HEAD_H);
    ctx.strokeStyle = toRgba(P.primary, 0.16); ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, HEAD_H); ctx.lineTo(BW, HEAD_H); ctx.stroke();
    const trkY = HEAD_H - 7;
    ctx.strokeStyle = toRgba(P.struct, 0.5); ctx.lineWidth = 2.5; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(14, trkY); ctx.lineTo(BW - 14, trkY); ctx.stroke(); ctx.lineCap = 'butt';
    if (idle) {
      const tx = laneX(aim.current);
      ctx.beginPath(); ctx.arc(tx, trkY, 6.5, 0, Math.PI * 2); ctx.fillStyle = P.secondary; ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,.5)'; ctx.lineWidth = 1; ctx.stroke();
    }
    ctx.font = "700 10px 'Space Mono', monospace"; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    ctx.fillStyle = toRgba(P.fg, 0.9); ctx.fillText(wordmark.toLowerCase() + '™', 12, 15);
    ctx.textAlign = 'right'; ctx.font = "700 10px 'DM Sans', sans-serif"; ctx.fillStyle = toRgba(P.accent, 0.92);
    ctx.fillText('XINKIT', BW - 12, 15);
    if (idle) {
      ctx.textAlign = 'center'; ctx.font = "9px 'DM Sans', sans-serif"; ctx.fillStyle = P.fgSubtle;
      ctx.fillText(T.aimArrows[language], BW / 2, 15);
    }

    // (The single "Powered by Xotski" watermark is rendered as a div below the
    //  canvas — see the JSX — so it never overlaps the bottom bucket row.)

    // ── Board frame marquee (branded bezel + corner bolts) ──
    ctx.save();
    ctx.lineWidth = 3; ctx.strokeStyle = toRgba(P.struct, 0.55);
    rr(ctx, 5, 5, BW - 10, BH - 10, 13); ctx.stroke();
    ctx.lineWidth = 1; ctx.strokeStyle = toRgba(P.accent, 0.28);
    rr(ctx, 9, 9, BW - 18, BH - 18, 10); ctx.stroke();
    ctx.fillStyle = toRgba(P.accent, 0.5);
    [[14, 14], [BW - 14, 14], [14, BH - 14], [BW - 14, BH - 14]].forEach(([x, y]) => { ctx.beginPath(); ctx.arc(x, y, 2.3, 0, Math.PI * 2); ctx.fill(); });
    ctx.restore();
  }, [P, demoPhase, wordmark, tagline, language, drawBall, bucketColor, tierAt, prizeNameLabel]);

  // ── Demo freeze frames (one per state, target = jackpot center) ──
  useEffect(() => {
    if (!demoPhase) {
      mode.current = 'idle'; ball.current = { x: laneX(4), y: DROP_Y, vy: 1.5, squash: 0, trail: [], active: false, landed: false, bucket: -1 };
      sparks.current = []; burst.current = []; pegHit.current = {}; landFill.current = 0; reveal.current = 0;
      firedRef.current = false; setCanDrop(true);
      return;
    }
    mode.current = 'demo';
    const k = 4; // center jackpot for the canonical demo
    const bits = [1, 1, 1, 1, 0, 0, 0, 0]; const Sd = [4]; let c = 0;
    for (let m = 1; m <= ROWS; m++) { c += bits[m - 1]; Sd.push(4 - 0.5 * m + c); }
    const wp = waypoints(Sd, 4);
    ball.current.bucket = k;
    if (demoPhase === 'idle') {
      ball.current = { ...ball.current, active: false, landed: false, y: DROP_Y, x: laneX(aim.current), trail: [] };
      sparks.current = []; burst.current = []; pegHit.current = {};
    } else if (demoPhase === 'dropping') {
      const p = wp[3]; ball.current = { ...ball.current, active: true, landed: false, x: p.x, y: p.y, vy: 2.4, squash: 0.6, trail: [wp[1], wp[2], p] };
      pegHit.current = { [nearestPeg(wp[3].x, pegRowY(2))]: 8 };
      sparks.current = seedSparks(wp[3].x, wp[3].y); burst.current = [];
    } else if (demoPhase === 'inFlight') {
      const a = wp[4], bb = wp[5], p = { x: lerp(a.x, bb.x, 0.5), y: lerp(a.y, bb.y, 0.5) };
      ball.current = { ...ball.current, active: true, landed: false, x: p.x, y: p.y, vy: 1.8, squash: 0, trail: [a, p] };
      pegHit.current = {}; sparks.current = []; burst.current = [];
    } else if (demoPhase === 'landed') {
      ball.current = { ...ball.current, active: false, landed: true, x: laneX(k), y: BUCKET_Y, trail: [] };
      landFill.current = 0.68; burst.current = seedBurst(k); sparks.current = []; pegHit.current = {};
    } else if (demoPhase === 'winReveal') {
      ball.current = { ...ball.current, active: false, landed: true, x: laneX(k), y: BUCKET_Y, trail: [] };
      landFill.current = 1; reveal.current = 0.85; burst.current = seedBurst(k).slice(0, 10); sparks.current = []; pegHit.current = {};
    }
    force((n) => n + 1);
  }, [demoPhase, seedSparks, seedBurst]);

  // ── Update / physics-flavored animation following the authored path ──
  const update = useCallback(() => {
    idleT.current += 0.05;
    Object.keys(pegHit.current).forEach((k) => { const key = Number(k); pegHit.current[key]--; if (pegHit.current[key] <= 0) delete pegHit.current[key]; });
    if (sparks.current.length) sparks.current = sparks.current.map((s) => ({ ...s, x: s.x + s.vx, y: s.y + s.vy, vy: s.vy + 0.12, life: s.life - 0.05 })).filter((s) => s.life > 0);
    if (burst.current.length) burst.current = burst.current.map((p) => ({ ...p, x: p.x + p.vx, y: p.y + p.vy, vy: p.vy + 0.12, life: p.life - 0.022 })).filter((p) => p.life > 0);

    if (demoPhase) {
      if (demoPhase === 'winReveal') { reveal.current = Math.min(1, reveal.current + 0.04); if (burst.current.length === 0 && Math.random() < 0.04) burst.current = seedBurst(4).slice(0, 8); }
      return;
    }
    const b = ball.current;
    if (b.squash) b.squash *= 0.82;

    if (mode.current === 'dropping') {
      const wp = path.current; if (!wp) { mode.current = 'idle'; return; }
      const tgt = wp[seg.current];
      // Gravity + terminal velocity tuned so drop + one tension beat lands inside
      // the ~2.5-3s window (PRD §2 Locked Decision 8); no long decelerating tail.
      b.vy = Math.min(b.vy + 0.1, 3.0);
      const prev = wp[seg.current - 1] || wp[0];
      b.y += b.vy;
      let tY = (b.y - prev.y) / Math.max(1, tgt.y - prev.y); tY = clamp(tY, 0, 1);
      const ease = tY < 0.5 ? 2 * tY * tY : 1 - Math.pow(-2 * tY + 2, 2) / 2;
      const wob = Math.sin(tY * Math.PI) * (seg.current % 2 ? 1 : -1) * 4.5;
      b.x = lerp(prev.x, tgt.x, ease) + wob;
      b.trail.push({ x: b.x, y: b.y }); if (b.trail.length > 9) b.trail.shift();
      if (b.y >= tgt.y) {
        if (seg.current <= ROWS) {
          pegHit.current[nearestPeg(tgt.x, tgt.y)] = 8;
          sparks.current = sparks.current.concat(seedSparks(tgt.x, tgt.y));
          b.x = tgt.x; b.y = tgt.y; b.squash = 1; sndPeg(b.vy); buzz(7);
        }
        if (!hung.current && (seg.current - 1) === hangRow.current) {
          hung.current = true; mode.current = 'hang';
          teeter.current = { t0: performance.now(), sx: tgt.x, sy: tgt.y }; sndTensionStart();
        } else {
          seg.current++;
          if (seg.current > ROWS) {
            if (hangRow.current < 0) { mode.current = 'teeter'; teeter.current = { phase: 'toDecoy', t0: performance.now(), sx: b.x, sy: b.y }; }
            else { mode.current = 'settle'; teeter.current = { t0: performance.now(), sx: b.x, sy: b.y }; }
          }
        }
      }
    } else if (mode.current === 'hang') {
      const tt = teeter.current!, now = performance.now();
      b.x = tt.sx + Math.sin(now / 52) * 2.3; b.y = tt.sy - 3;
      if ((now - tt.t0) >= 400) {
        sndTensionStop(); sndPeg(2.4); buzz(7); b.squash = 1;
        mode.current = 'dropping'; seg.current++; b.vy = 0.7;
        if (seg.current > ROWS) { mode.current = 'settle'; teeter.current = { t0: performance.now(), sx: b.x, sy: b.y }; }
      }
    } else if (mode.current === 'settle') {
      const tt = teeter.current!, now = performance.now();
      const p = Math.min(1, (now - tt.t0) / 300), e = 1 - Math.pow(1 - p, 2);
      b.x = lerp(tt.sx, laneX(targetK.current), e); b.y = lerp(tt.sy, BUCKET_Y + BUCKET_H * 0.52, e);
      b.trail.push({ x: b.x, y: b.y }); if (b.trail.length > 9) b.trail.shift();
      if (p >= 1) { b.active = false; b.landed = true; b.bucket = targetK.current; sndThud(); buzz([14, 26, 38]); mode.current = 'landed'; landFill.current = 0; burst.current = seedBurst(targetK.current); }
    } else if (mode.current === 'teeter') {
      const tt = teeter.current!, now = performance.now();
      const dX = laneX(decoyD.current), tX = laneX(targetK.current);
      const mouthY = BUCKET_Y + 8, restY = BUCKET_Y + BUCKET_H * 0.52;
      if (tt.phase === 'toDecoy') {
        const p = Math.min(1, (now - tt.t0) / 300), e = 1 - Math.pow(1 - p, 2);
        b.x = lerp(tt.sx, dX, e); b.y = lerp(tt.sy, mouthY, e);
        b.trail.push({ x: b.x, y: b.y }); if (b.trail.length > 9) b.trail.shift();
        if (p >= 1) { tt.phase = 'hold'; tt.t0 = now; b.squash = 1; sndPeg(2.6); buzz(7); sndTensionStart(); pegHit.current[nearestPeg(dX, BUCKET_Y)] = 8; sparks.current = sparks.current.concat(seedSparks(dX, mouthY)); }
      } else if (tt.phase === 'hold') {
        b.x = dX + Math.sin(now / 55) * 1.7; b.y = mouthY;
        if ((now - tt.t0) >= 430) { tt.phase = 'toTarget'; tt.t0 = now; tt.sx = b.x; sndTensionStop(); }
      } else {
        const p = Math.min(1, (now - tt.t0) / 360), e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        b.x = lerp(tt.sx, tX, e); b.y = lerp(mouthY, restY, e);
        b.trail.push({ x: b.x, y: b.y }); if (b.trail.length > 9) b.trail.shift();
        if (p >= 1) { b.active = false; b.landed = true; b.bucket = targetK.current; sndThud(); buzz([14, 26, 38]); mode.current = 'landed'; landFill.current = 0; burst.current = seedBurst(targetK.current); }
      }
    } else if (mode.current === 'landed') {
      landFill.current = Math.min(1, landFill.current + 1 / 36);
      if (landFill.current >= 1) { mode.current = 'reveal'; reveal.current = 0; sndWin(); buzz([0, 40, 30, 60]); }
    } else if (mode.current === 'reveal') {
      reveal.current = Math.min(1, reveal.current + 0.05);
      if (reveal.current >= 1 && !firedRef.current) {
        firedRef.current = true;
        const k = ball.current.bucket;
        window.setTimeout(() => { mode.current = 'done'; onRevealed?.(prize, k); }, 300);
      }
    }
  }, [demoPhase, seedSparks, seedBurst, onRevealed, prize]);

  // ── Animation loop ──
  useEffect(() => {
    let id: number;
    draw(); // paint one frame immediately (rAF may be throttled when hidden)
    const loop = () => { update(); draw(); id = requestAnimationFrame(loop); };
    id = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(id);
  }, [update, draw]);

  // ── Drop (engine-first). prize chosen by caller; we author the path. ──
  const drop = useCallback(() => {
    if (mode.current !== 'idle') return;
    setCanDrop(false);
    const k = targetBucket(prize.rating ?? 3, aim.current, tierAt);
    const S = authorPath(k);
    const wp = waypoints(S, aim.current);
    const d = k <= 0 ? 1 : k >= 8 ? 7 : k + (Math.random() < 0.5 ? 1 : -1);
    targetK.current = k; decoyD.current = d; teeter.current = null;
    hung.current = false;
    hangRow.current = Math.random() < 0.5 ? -1 : (2 + Math.floor(Math.random() * 5));
    if (reducedMotion) {
      // prefers-reduced-motion: straight into the target bucket, no bounces.
      ball.current = { ...ball.current, active: false, landed: true, bucket: k, x: laneX(k), y: BUCKET_Y };
      landFill.current = 1; burst.current = seedBurst(k); reveal.current = 0; firedRef.current = false; mode.current = 'reveal';
      return;
    }
    path.current = wp; seg.current = 1; firedRef.current = false;
    audioCtx(); sndTensionStop(); // unlock audio on the Drop gesture; clear any held note
    ball.current = { x: wp[0].x, y: wp[0].y, vy: 0.9, squash: 0, trail: [], active: true, landed: false, bucket: -1 };
    pegHit.current = {}; sparks.current = []; burst.current = []; landFill.current = 0; reveal.current = 0;
    mode.current = 'dropping';
  }, [prize, tierAt, reducedMotion, seedBurst]);

  // ── Drag to aim (idle only) ──
  const setAimFromEvent = (clientX: number) => {
    if (mode.current !== 'idle') return;
    const cv = cvRef.current; if (!cv) return;
    const r = cv.getBoundingClientRect();
    const px = (clientX - r.left) * (BW / r.width);
    aim.current = clamp(px / BKT_W - 0.5, 0, 8);
  };
  const dragging = useRef(false);
  const onDown = (e: React.PointerEvent) => { if (demoPhase || mode.current !== 'idle') return; dragging.current = true; cvRef.current?.setPointerCapture(e.pointerId); setAimFromEvent(e.clientX); };
  const onMove = (e: React.PointerEvent) => { if (!dragging.current) return; setAimFromEvent(e.clientX); };
  const onUp = () => { dragging.current = false; };

  // Keyboard aim + drop (accessibility: native non-gesture path).
  const onKey = (e: React.KeyboardEvent) => {
    if (demoPhase || mode.current !== 'idle') return;
    if (e.key === 'ArrowLeft') { aim.current = clamp(aim.current - 0.5, 0, 8); e.preventDefault(); }
    else if (e.key === 'ArrowRight') { aim.current = clamp(aim.current + 0.5, 0, 8); e.preventDefault(); }
    else if (e.key === 'Enter' || e.key === ' ') { drop(); e.preventDefault(); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
      <canvas
        ref={cvRef}
        width={BW}
        height={BH}
        role="img"
        tabIndex={demoPhase ? -1 : 0}
        aria-label={
          mode.current === 'reveal' || mode.current === 'done' || demoPhase === 'winReveal'
            ? `You won ${prizeNameLabel}`
            : `${T.hint[language]}.`
        }
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
        onKeyDown={onKey}
        style={{
          display: 'block', width: '100%', maxWidth: BW, height: 'auto', borderRadius: 16,
          border: `1px solid ${toRgba(brand.primary, 0.18)}`,
          boxShadow: `0 0 40px ${toRgba(brand.primary, 0.28)}`,
          touchAction: 'none', WebkitTapHighlightColor: 'transparent', userSelect: 'none',
          cursor: demoPhase ? 'default' : (canDrop ? 'ew-resize' : 'default'),
        }}
      />

      {/* Non-gesture fallback (accessibility): drop without a flick */}
      {!demoPhase && (
        <button
          type="button"
          onClick={() => drop()}
          disabled={!canDrop}
          style={{
            border: `1.5px solid ${brand.accent}`, color: canDrop ? brand.accent : toRgba(brand.accent, 0.4),
            background: 'transparent', borderRadius: 999, padding: '8px 30px', fontSize: 14, fontWeight: 700,
            cursor: canDrop ? 'pointer' : 'default', fontFamily: "'DM Sans', sans-serif", letterSpacing: 0.5,
          }}
        >
          ↓ {T.drop[language]}
        </button>
      )}

      {/* Powered by Xotski — 8px, bottom-center, Xotski lime, non-configurable */}
      <div aria-hidden style={{ fontSize: 8, letterSpacing: 0.5 }}>
        <span style={{ color: 'rgba(245,243,255,0.5)' }}>Powered by </span>
        <span style={{ color: XOTSKI_LIME, fontWeight: 700 }}>Xotski</span>
      </div>
    </div>
  );
};

export default Xinkit;
